import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../../services/api';
import { HiTrash } from 'react-icons/hi';
import { FiEdit, FiEye } from "react-icons/fi";
import Tooltip from '../../components/tooltip';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from 'styled-components';
import {
  Container,
  Header,
  Title,
  AddButton,
  RuleList,
  RuleItem,
  ActionButtons,
  EditButton,
  DeleteButton,
  DeleteModal,
  DeleteModalContent,
  DeleteModalButtons,
  CancelButton,
  ConfirmButton,
  RuleInfo,
  RuleInfoItem,
  RuleInfoLabel,
  RuleInfoValue
} from './styles';
import Loading from '../../components/Loading';
import styled from 'styled-components';

// Botão azul para OK na modal de sucesso
const BlueButton = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  background-color: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.primaryOn};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${({ theme }) => theme.primary};
  }
`;

const CustomDeleteModal = styled(DeleteModal)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999 !important;
`;

// Estilizando o Link para se parecer com o botão EditButton
const StyledLink = styled(Link)`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.primaryAccent};
  transition: opacity 0.2s;

  .icon {
    width: 20px;
    height: 20px;
  }

  &:hover {
    opacity: 0.8;
  }
`;

interface Rule {
  _id: string;
  name: string;
  description: string;
  category: string;
  severity: string;
  status: string;
}

const Rules: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [rules, setRules] = useState<Rule[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const fetchRef = useRef(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successAction, setSuccessAction] = useState('');
  const [ruleToDelete, setRuleToDelete] = useState<string | null>(null);
  const [deletionCompleted, setDeletionCompleted] = useState(false);
  const [organizationId, setOrganizationId] = useState<string>('');

  useEffect(() => {
    // Verifica se deve mostrar a modal de sucesso
    const shouldShowSuccessModal = localStorage.getItem('@App:showSuccessModal');
    const action = localStorage.getItem('@App:successAction');
    
    if (shouldShowSuccessModal === 'true') {
      setShowSuccessModal(true);
      setSuccessAction(action || '');
      
      // Limpar o localStorage
      localStorage.removeItem('@App:showSuccessModal');
      localStorage.removeItem('@App:successAction');
    }
  }, []);

  useEffect(() => {
    const fetchRules = async () => {
      if (fetchRef.current) return; // Evita chamadas duplicadas
      fetchRef.current = true;

      setIsLoading(true);
      try {
        const token = localStorage.getItem('@App:token');
        
        // Verificar se o usuário tem organizationId
        if (!user || !user.organizationId) {
          throw new Error('Nenhuma organização encontrada para o usuário');
        }
        
        // Se organizationId for um array, usa o primeiro
        const orgId = Array.isArray(user.organizationId) 
          ? user.organizationId[0] 
          : user.organizationId;
        
        setOrganizationId(orgId);
        
        // Usar o novo endpoint para buscar as regras da organização
        const response = await api.get(`/api/prompt/organization/${orgId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        console.log('Rules loaded from organization:', response.data);
        
        // Adequar o formato dos dados se necessário
        const formattedRules = response.data.map((rule: any) => ({
          _id: rule._id || rule.id,
          name: rule.name,
          description: rule.description || 'Sem descrição',
          category: rule.category || 'Não categorizado',
          severity: rule.severity || 'Medium',
          status: rule.status || 'Active'
        }));
        
        setRules(formattedRules);
      } catch (error) {
        console.error('Error loading rules from API:', error);
        setRules([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRules();
  }, [user]);

  const handleAdd = () => {
    navigate(`/rules/new?idOrganization=${organizationId}`);
  };

  const handleDelete = (id: string) => {
    // Se já completou uma exclusão anteriormente, reseta o estado primeiro
    if (deletionCompleted) {
      setDeletionCompleted(false);
      setShowSuccessModal(false);
    }
    
    setRuleToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!ruleToDelete) return;
    
    setShowDeleteModal(false);
    setIsLoading(true);
    
    try {
      // Chamada real para a API
      const token = localStorage.getItem('@App:token');
      await api.delete(`/api/prompt/${ruleToDelete}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Update the list by removing the deleted rule
      setRules(rules.filter(rule => rule._id !== ruleToDelete));
      
      // Mostrar a modal de sucesso diretamente ao invés de usar localStorage
      setSuccessAction('deleted');
      setShowSuccessModal(true);
      setDeletionCompleted(true);
      
    } catch (error) {
      console.error('Error deleting rule:', error);
      // Em caso de erro, também tentamos remover da lista local
      setRules(rules.filter(rule => rule._id !== ruleToDelete));
      setSuccessAction('deleted');
      setShowSuccessModal(true);
      setDeletionCompleted(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
  };

  return (
    <Container>
      <Header>
        <Title>{t('rules.title')}</Title>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Tooltip text={t('rules.addNew')}>
            <AddButton onClick={handleAdd}>
              {t('common.new')}
            </AddButton>
          </Tooltip>
        </div>
      </Header>

      <RuleList>
        {isLoading ? (
          <Loading />
        ) : (
          rules.map(rule => (
            <RuleItem key={rule._id}>
              <RuleInfo>
                <RuleInfoItem>
                  <RuleInfoLabel>{t('rules.name')}:</RuleInfoLabel>
                  <RuleInfoValue>{rule.name}</RuleInfoValue>
                </RuleInfoItem>
                {rule.description && (
                  <RuleInfoItem>
                    <RuleInfoLabel>{t('rules.description')}:</RuleInfoLabel>
                    <RuleInfoValue>{rule.description}</RuleInfoValue>
                  </RuleInfoItem>
                )}
                {rule.category && (
                  <RuleInfoItem>
                    <RuleInfoLabel>{t('rules.category')}:</RuleInfoLabel>
                    <RuleInfoValue>{rule.category}</RuleInfoValue>
                  </RuleInfoItem>
                )}
                {rule.severity && (
                  <RuleInfoItem>
                    <RuleInfoLabel>{t('rules.severity')}:</RuleInfoLabel>
                    <RuleInfoValue style={{
                      /*
                        Severidade é semântica de estado: alta = erro, média = aviso, baixa =
                        sucesso. Os valores eram o rosa e o verde do Twitter mais um laranja
                        avulso — três cores que não pertencem a este produto.
                      */
                      color: rule.severity === 'High' ? theme.error :
                             rule.severity === 'Medium' ? theme.warning : theme.success
                    }}>
                      {rule.severity}
                    </RuleInfoValue>
                  </RuleInfoItem>
                )}
              </RuleInfo>
              <ActionButtons>
                <Tooltip text={t('common.view')}>
                  <StyledLink to={`/rules/details/${rule._id}?idOrganization=${organizationId}`}>
                    {FiEye({ className: "icon", size: 20 })}
                  </StyledLink>
                </Tooltip>
                <Tooltip text={t('common.edit')}>
                  <StyledLink to={`/rules/edit/${rule._id}?idOrganization=${organizationId}`}>
                    {FiEdit({ className: "icon", size: 20 })}
                  </StyledLink>
                </Tooltip>
                <Tooltip text={t('common.delete')}>
                  <DeleteButton onClick={() => handleDelete(rule._id)}>
                    {HiTrash({ className: "icon", size: 20 })}
                  </DeleteButton>
                </Tooltip>
              </ActionButtons>
            </RuleItem>
          ))
        )}
      </RuleList>

      {showDeleteModal && !deletionCompleted && (
        <CustomDeleteModal>
          <DeleteModalContent>
            <h3>{t('common.delete')} {t('rules.title')}</h3>
            <p>{t('rules.confirmDelete')}</p>
            <DeleteModalButtons>
              <CancelButton onClick={() => setShowDeleteModal(false)}>
                {t('common.cancel')}
              </CancelButton>
              <ConfirmButton onClick={confirmDelete}>
                {t('common.delete')}
              </ConfirmButton>
            </DeleteModalButtons>
          </DeleteModalContent>
        </CustomDeleteModal>
      )}

      {showSuccessModal && (
        <CustomDeleteModal>
          <DeleteModalContent>
            <h3>{t('common.success')}</h3>
            <p>{successAction === 'updated' ? t('rules.successUpdated') : t('rules.successMessage')}</p>
            <DeleteModalButtons>
              <BlueButton onClick={handleSuccessModalClose}>
                OK
              </BlueButton>
            </DeleteModalButtons>
          </DeleteModalContent>
        </CustomDeleteModal>
      )}
    </Container>
  );
};

export default Rules; 